import classnames from 'classnames';
import React, { useMemo } from 'react';

import { getBreederProfileUrl } from 'dpl/breeder_search_results/mobile/components/Card';
import UntypedCardMediaCarousel from 'dpl/breeder_search_results/mobile/components/CardMediaCarousel';
import { MOBILE_LAYOUTS } from 'dpl/components/BreederProfile/utils/constants';
import {
  ANALYTICS_EVENTS,
  ANALYTICS_VIEW_FEATURES,
  ANALYTICS_VIEW_FEATURE_LOCATIONS
} from 'dpl/constants/analytics';
import { MOBILE_LAYOUT } from 'dpl/constants/query_params';
import UserAvatar from 'dpl/path_to_payment/components/UserAvatar';
import { getAnalyticsOrigin } from 'dpl/shared/utils/getAnalyticsOrigin';
import { setAnalyticsOrigin } from 'dpl/shared/utils/setAnalyticsOrigin';
import { sendRudderstackEvent } from 'dpl/util/analytics';

/**
 * Square photos, sized so the strip is exactly as tall as the text column
 * beside it (14px title over 14px distance, then a 32px avatar row).
 */
const PHOTO_SIZE = 101;

/**
 * The text column is fixed rather than fluid so every card's photo strip starts
 * at the same x, which is what makes the strips read as one column while each
 * scrolls on its own.
 */
const LEFT_COLUMN_WIDTH = 176;

const SMART_IMAGE_PROPS = {
  Tag: 'div',
  className: 'bg-center cover',
  crop: true,
  loadingClass: 'bg-light-gray',
  height: `${PHOTO_SIZE}px`,
  width: `${PHOTO_SIZE}px`,
  allowContextMenu: true
};

/**
 * The card's 20px inset would push the first photo away from the text column;
 * here the strip starts flush and bleeds off the right edge instead.
 */
const SPLIDE_OPTIONS = { padding: { left: 0, right: 16 } };

/**
 * `CardMediaCarousel` is a `React.memo` in a .jsx file, so `withLazyLoading`
 * infers no props for it and TypeScript rejects every one we pass. Naming the
 * props we actually use restores checking at this call site.
 */
const CardMediaCarousel = UntypedCardMediaCarousel as React.ComponentType<{
  mediaFiles: { id: number | string; url: string; alt: string }[];
  smartImageProps: Record<string, unknown>;
  splideOptions?: Record<string, unknown>;
  analytics?: Record<string, unknown>;
}>;

export interface SimilarBreedsBreederPhoto {
  id: number | string;
  url: string;
  alt_text?: string | null;
}

export interface SimilarBreedsBreeder {
  id: number;
  user_id: number;
  initials: string;
  name: string;
  display_name: string;
  url: string;
  breed_id: number;
  breed_name: string;
  available_puppy_count: number;
  distance_description: string | null;
  profile_photo_url?: string | null;
  cropped_photo_url?: string | null;
  photographs?: SimilarBreedsBreederPhoto[];
}

interface SimilarBreedsBreederCardProps {
  breeder: SimilarBreedsBreeder;
  position: number;
  view: string;
  className?: string | null;
}

/**
 * One card of the breeder-list arm: a fixed text column describing the
 * breeder's availability, and a horizontally scrolling strip of their puppy
 * photos.
 */
export default function SimilarBreedsBreederCard({
  breeder,
  position,
  view,
  className = null
}: SimilarBreedsBreederCardProps) {
  const {
    id: breederProfileId,
    user_id: userId,
    initials,
    name,
    display_name: displayName,
    url,
    breed_id: breedId,
    breed_name: breedName,
    available_puppy_count: availablePuppyCount,
    distance_description: distanceDescription,
    profile_photo_url: profilePhotoUrl,
    cropped_photo_url: croppedPhotoUrl,
    photographs
  } = breeder;

  const mediaFiles = useMemo(
    () =>
      (photographs || []).map(photo => ({
        ...photo,
        alt: photo.alt_text || `${breedName} puppy raised by ${name}`
      })),
    [photographs, breedName, name]
  );

  const analytics = {
    view,
    view_feature: ANALYTICS_VIEW_FEATURES.BREEDER_CARD,
    view_feature_location:
      ANALYTICS_VIEW_FEATURE_LOCATIONS.SIMILAR_BREED_PUPPIES,
    breeder_profile_id: breederProfileId,
    breed_id: breedId,
    position
  };

  function handleClick() {
    // The carousel arm's cards get this from the shared puppy card. Without the
    // same here, a contact that started on this arm would be indistinguishable
    // from one that started in the feed - and the two arms are compared on
    // exactly that. `originView` is carried over rather than named, the way the
    // puppy card does it: the module sits inside the feed's own view.
    setAnalyticsOrigin({
      originView: getAnalyticsOrigin().origin_view,
      originViewFeature: ANALYTICS_VIEW_FEATURES.BREEDER_CARD,
      originViewFeatureLocation:
        ANALYTICS_VIEW_FEATURE_LOCATIONS.SIMILAR_BREED_PUPPIES
    });

    sendRudderstackEvent(ANALYTICS_EVENTS.CARD_CLICKED_BY_USER, analytics);
  }

  return (
    <a
      className={classnames(
        'SimilarBreedsBreederCard db pv3 pl4 bb bt b--default bg-white',
        className
      )}
      href={getBreederProfileUrl(url, { [MOBILE_LAYOUT]: MOBILE_LAYOUTS.NEW })}
      onClick={handleClick}
      draggable="false"
    >
      <div className="grid grid-cols-2 gap-16">
        <div
          className="flex flex-column gap-12"
          style={{ width: LEFT_COLUMN_WIDTH }}
        >
          <div className="flex flex-column gap-4">
            <p className="font-14 fw-medium lh-title">
              {availablePuppyCount} {breedName}{' '}
              {availablePuppyCount === 1 ? 'puppy' : 'puppies'} available
            </p>
            {distanceDescription && (
              <p className="font-14 tertiary lh-title">{distanceDescription}</p>
            )}
          </div>
          <div className="flex items-start gap-8">
            <UserAvatar
              className="flex-none"
              user={{
                id: userId,
                initials,
                profile_photo_url: croppedPhotoUrl || profilePhotoUrl
              }}
              size="sm"
            />
            <p className="font-14 lh-title truncate">
              Raised by
              <br />
              {displayName}
            </p>
          </div>
        </div>
        {/* Clips the strip's overflow to the card so only it scrolls, leaving
            the text column fixed. */}
        <div className="overflow-hidden">
          <CardMediaCarousel
            mediaFiles={mediaFiles}
            smartImageProps={SMART_IMAGE_PROPS}
            splideOptions={SPLIDE_OPTIONS}
            analytics={analytics}
          />
        </div>
      </div>
    </a>
  );
}
